<script setup>
import { computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

// A labelled contact selector backed by the shared contacts store. It wraps
// ComboBox in API-results mode: typing searches the account's contacts and the
// selected id is exposed through v-model, with the full contact object emitted
// alongside so callers can derive display details without a second lookup.
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue', 'select']);
const { t } = useI18n();
const globalStore = useStore();

const contacts = computed(() => globalStore.getters['contacts/getContactsList'] || []);

const contactOptions = computed(() =>
  contacts.value.map(contact => ({
    value: contact.id,
    label: contact.name || contact.email || t('DOCUMENTS.CONTACT_PICKER.UNTITLED'),
  }))
);

const resolvedPlaceholder = computed(
  () => props.placeholder || t('DOCUMENTS.CONTACT_PICKER.PLACEHOLDER')
);

const loadFirstPage = () => {
  globalStore.dispatch('contacts/get', { page: 1 });
};

const handleSearch = query => {
  if (query.length >= 2) {
    globalStore.dispatch('contacts/search', { search: query });
  } else if (!query.length) {
    loadFirstPage();
  }
};

const handleSelect = value => {
  emit('update:modelValue', value);
  emit('select', contacts.value.find(contact => contact.id === value) || null);
};

// Parents call load() when their dialog opens so contacts are fetched lazily
// rather than on page mount, which would clobber the shared contacts store.
const load = () => loadFirstPage();
defineExpose({ load });
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <label v-if="label" class="block text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>
    <ComboBox
      :model-value="modelValue"
      :options="contactOptions"
      :placeholder="resolvedPlaceholder"
      :search-placeholder="t('DOCUMENTS.CONTACT_PICKER.SEARCH_PLACEHOLDER')"
      :empty-state="t('DOCUMENTS.CONTACT_PICKER.EMPTY')"
      :has-error="hasError"
      use-api-results
      @update:model-value="handleSelect"
      @search="handleSearch"
    />
  </div>
</template>
